// Tier por PLATAFORMA/MOTOR — a fonte determinística de "que classe de device é
// este", com PRECEDÊNCIA sobre o microbench.
//
// ## Por que plataforma > bench (o princípio, agora levado a sério)
//
// O microbench (`benchmark.ts`) mede JS single-thread. O que trava TV velha não
// é JS — é compositing de GPU (blur/shadow/transform) e memória; e o que o
// preview de TV ao Vivo custa é DECODE, que em TV é HARDWARE. O bench não mede
// nenhum dos dois. A geração do MOTOR/OS é o proxy correto e DETERMINÍSTICO.
//
// Antes, "precedência" estava implementada só como TETO (`min(bench, teto)`) —
// o que na prática deixava o bench mandar, porque **o bench de JS de qualquer TV
// é lento**. Medido na TV do dono (LG 55SM8600PSA, 2019, quad-core, 4K) com o
// bench DESTE repo, via `ares-inspect` no device:
//
//   rodadas: [94.02, 84.57, 49.76, 49.55, 50.51] ms  →  mediana 50.5ms = 'low'
//   e a 1ª rodada (a que o boot real pega, com JIT frio) = 94ms = 'vlow'
//
// Ou seja: o bench sozinho classificava uma TV 2019 capaz como 'low'/'vlow',
// e como `deviceMemory` é `undefined` no webOS (ram.tier='unknown'), a CPU
// decidia SOZINHA. Teto nenhum consertava isso — teto só puxa pra baixo.
//
// Por isso a plataforma agora devolve uma **BANDA** (piso + teto):
//   - **webOS** (MEDIDO numa LG real) → a plataforma DECIDE (piso=teto). Sabemos
//     a classe do hardware; o bench só adicionaria ruído de boot. O PISO só se
//     justifica porque foi medido no device (ver bench da LG acima).
//   - **Tizen** (NÃO medido — ver `platform-native.ts` "NÃO TESTADO EM DEVICE
//     REAL") → só TETO. O piso PROMOVE tier (liga preview HLS/autoplay no hero),
//     e promover às cegas um hardware que ninguém rodou é justamente exportar
//     pra Samsung a lentidão que consertamos na LG. Sem medição, o teto protege
//     a TV velha e o bench decide pra baixo — conservador, como era antes.
//     Quando alguém medir uma Samsung real, o piso pode ser estendido ao Tizen.
//   - Device DESCONHECIDO (Android box genérico, VIDAA, web) → só TETO, como
//     antes. Sem sinal de classe, o bench continua sendo o melhor que temos, e
//     o teto protege contra motor antigo.
//
// ## Calibração (versões reais, não supostas)
//
// webOS: Chromium 38 = webOS ≤3 (2016-2017) · 53 = webOS 4 (2018-2019) ·
//        68 = webOS 5 (2020) · 79+ = webOS 6+ (2021+).
// Tizen: Chromium 47 = Tizen ≤3 (2017-) · 56 = Tizen 4 (2018) · 63 = Tizen 5
//        (2019) · 69 = Tizen 5.5 (2020) · 76+ = Tizen 6+ (2021+).
// Casa com `docs/specs/tv-compat.md` §0 ("WebView Chromium 53, webOS 4.x/2019,
// TV LG real do Fernando; piso Chromium 38, webOS 3.5/2017").
//
// O resultado é classificar CERTO, não inflar: a LG de 2017 (webOS 3, Chr38)
// continua **vlow** — hard cap, o bench não a resgata; a de 2018-2020 vira
// **mid**; a de 2021+ tem piso mid e o bench pode levá-la a high/ultra. O
// Tizen usa a MESMA escada de TETO (protege TV velha), mas SEM o piso — a
// Samsung nunca foi medida, então o bench manda pra baixo (ver `bandaTizenPorGeracao`).

import type { Platform, Tier } from '@app-iptv/shared'

export interface PlatformTierInput {
  os: Platform
  /** Versão da plataforma. `'0'`/vazio = DESCONHECIDA — e "desconhecida" NÃO
   *  significa "pior caso": significa "use o `chromiumVersion`". Era exatamente
   *  esse o bug (a LG nunca emite a versão no UA → toda LG virava vlow). */
  version: string
  /** Major do motor Chromium (ver `platform.ts`). null quando ausente. */
  chromiumVersion: number | null
}

/** Faixa de tier imposta pela plataforma.
 *  - `floor`: tier MÍNIMO. A plataforma garante esta classe — o bench não pode
 *    rebaixar abaixo disso. `null` = sem piso.
 *  - `ceiling`: tier MÁXIMO. `null` = sem teto (bench livre pra cima). */
export interface TierBand {
  floor: Tier | null
  ceiling: Tier | null
}

const SEM_BANDA: TierBand = { floor: null, ceiling: null }

export const TIER_RANK: Record<Tier, number> = {
  vlow: 0,
  low: 1,
  mid: 2,
  high: 3,
  ultra: 4,
}
const RANK_TO_TIER: Tier[] = ['vlow', 'low', 'mid', 'high', 'ultra']

/** Extrai o MAJOR da versão da plataforma.
 *
 *  ⚠️ **NÃO usar `parseFloat`** aqui: o webOS da TV do dono reporta
 *  `sdkVersion: "4.10.0"`, e `parseFloat("4.10.0") === 4.1` — que compara como
 *  MENOR que `4.5`, embora 4.10 seja mais NOVO. Versão é semver, não número.
 *  Só o major nos interessa pro mapeamento de geração, o que mata a armadilha
 *  do minor de vez. */
export function parsePlatformMajor(version: string): number | null {
  const m = /^\s*(\d+)/.exec(version)
  if (!m) return null
  const major = parseInt(m[1] ?? '', 10)
  if (!Number.isFinite(major) || major <= 0) return null
  return major
}

/** Geração do webOS. Prefere a versão REAL (`sdkVersion`, via API nativa —
 *  `platform-native.ts`); cai no motor Chromium quando ela não veio (a LG não
 *  põe a versão no UA, então este fallback é o caminho QUENTE, não a exceção). */
function webosGeneration(version: string, chromium: number | null): number | null {
  const major = parsePlatformMajor(version)
  if (major !== null) return major
  if (chromium === null) return null
  if (chromium <= 38) return 3 // webOS 1-3 (2014-2017)
  if (chromium <= 53) return 4 // webOS 4.x (2018-2019)
  if (chromium <= 68) return 5 // webOS 5.x (2020)
  return 6 // webOS 6+ (2021+)
}

/** Geração do Tizen. A Samsung PÕE a versão no UA (`Tizen 6.0`), então o
 *  caminho normal é o major; o Chromium é só reforço. */
function tizenGeneration(version: string, chromium: number | null): number | null {
  const major = parsePlatformMajor(version)
  if (major !== null) return major
  if (chromium === null) return null
  if (chromium <= 47) return 3 // Tizen 2.x-3.x (2015-2017)
  if (chromium <= 56) return 4 // Tizen 4.x (2018)
  if (chromium <= 69) return 5 // Tizen 5.x-5.5 (2019-2020)
  return 6 // Tizen 6+ (2021+)
}

/** Banda de tier de uma TV **LG (webOS)** por GERAÇÃO. MEDIDA em device real
 *  (LG 55SM8600PSA, webOS 4.10, ver header) — por isso o PISO existe: só o
 *  device provou que o hardware aguenta o tier promovido.
 *  - gen ≤3 (2017-, Chr38): **vlow travado** — a TV fraca de verdade continua
 *    protegida, e o bench (que pode dar 'mid' num burst) não a resgata.
 *  - gen 4-5 (2018-2020, Chr53/68): **mid travado** — quad-core, 4K, decode em
 *    hardware. Não é 'low' (subestima) nem 'high' (o motor não tem
 *    `backdrop-filter`, que só chega no Chromium 76).
 *  - gen ≥6 (2021+, Chr76/79+): piso **mid**, sem teto — bench pode levar a
 *    high/ultra. */
function bandaWebosPorGeracao(gen: number | null): TierBand {
  if (gen === null) return SEM_BANDA
  if (gen <= 3) return { floor: 'vlow', ceiling: 'vlow' }
  if (gen <= 5) return { floor: 'mid', ceiling: 'mid' }
  return { floor: 'mid', ceiling: null }
}

/** Banda de tier de uma TV **Samsung (Tizen)** por GERAÇÃO. **Só TETO, NUNCA
 *  piso** — e a diferença é deliberada, não esquecimento.
 *
 *  O piso do webOS foi VALIDADO na LG real; o Tizen **NÃO foi testado em device
 *  algum** (`platform-native.ts` §Tizen). Herdar o piso do webOS travaria uma
 *  Samsung 2018 fraca (Tizen 4/Chr56) em `mid` — o bench não conseguiria
 *  rebaixá-la —, o que LIGA o autoplay do preview HLS no hero num hardware que
 *  ninguém mediu: exatamente a lentidão que acabamos de consertar na LG,
 *  exportada às cegas pra Samsung. O TETO (proteger TV velha) é seguro sem
 *  medição; o PISO (promover tier) não é. Sem piso, o benchmark decide pra
 *  baixo — o comportamento conservador de antes da banda.
 *
 *  Quando houver medição numa Samsung real, estenda o piso aqui (idealmente
 *  reusando `bandaWebosPorGeracao`, já que as gerações são contemporâneas).
 *  - gen ≤3 (2017-, Chr47): teto **vlow** (piso implícito: vlow já é o mínimo).
 *  - gen 4-5 (2018-2020, Chr56/69): teto **mid** — cap protege TV velha, mas o
 *    bench pode (e deve) rebaixar pra low/vlow sem medição.
 *  - gen ≥6 (2021+, Chr76+): SEM banda — nem piso (não medido) nem teto (motor
 *    moderno); bench livre. */
function bandaTizenPorGeracao(gen: number | null): TierBand {
  if (gen === null) return SEM_BANDA
  if (gen <= 3) return { floor: null, ceiling: 'vlow' }
  if (gen <= 5) return { floor: null, ceiling: 'mid' }
  return SEM_BANDA
}

/** Banda de tier imposta pela plataforma/motor.
 *
 *  Ordem: o **webOS** (medido) DECIDE por geração (piso+teto); o **Tizen** (não
 *  medido) ganha só TETO por geração; Fire TV e device desconhecido ganham só
 *  TETO, preservando o comportamento conservador de antes pra hardware sobre o
 *  qual não sabemos nada. */
export function platformTierBand(p: PlatformTierInput): TierBand {
  if (p.os === 'webos') return bandaWebosPorGeracao(webosGeneration(p.version, p.chromiumVersion))
  if (p.os === 'tizen') return bandaTizenPorGeracao(tizenGeneration(p.version, p.chromiumVersion))

  // Fire TV — versionado pelo Android, que o UA SEMPRE expõe. Só teto: os
  // modelos que importam já estão em `models.ts::KNOWN_MODELS` (AFT*), que é
  // sinal mais exato do que a geração.
  const major = parsePlatformMajor(p.version)
  if (p.os === 'firetv' && major !== null) {
    if (major < 6) return { floor: null, ceiling: 'vlow' }
    if (major < 7) return { floor: null, ceiling: 'low' }
    return SEM_BANDA
  }

  // Device DESCONHECIDO (Android box genérico, VIDAA, web) — motor antigo é o
  // único sinal. Só TETO, nunca piso: sem saber a classe do hardware, deixamos
  // o bench decidir pra baixo.
  if (p.chromiumVersion !== null) {
    if (p.chromiumVersion < 55) return { floor: null, ceiling: 'vlow' }
    if (p.chromiumVersion < 60) return { floor: null, ceiling: 'low' }
  }

  return SEM_BANDA
}

/**
 * Classe de device que toca MPEG-TS no `<video>` NATIVO — sem mpegts.js/MSE,
 * apontando `video.src` direto pra URL `.ts` (decode em HARDWARE, punch-through
 * no plano de vídeo). ALLOWLIST por PROVA, deliberadamente CONSERVADORA.
 *
 * ⚠️ NÃO derive isto de `canPlayType('video/mp2t')`: no fleet ele devolve
 * `'maybe'` (fraco/mentiroso) e ligaria o ramo nativo em TV NÃO medida. O gate é
 * plataforma+geração DETECTADA — o MESMO input de `platformTierBand`.
 *
 * - **webOS gen 4-5** (Chromium 53-68, 2018-2020) → `true`. CONFIRMADO em device
 *   real (LG 55SM8600PSA, webOS 4.10 / Chr53 — a prova do dono): `<video
 *   src=".ts">` cru dá `loadeddata` em ~1,8-2,0s com imagem e decode em HW. É a
 *   MESMA classe `mid` que `bandaWebosPorGeracao` já reconhece (gen 4-5).
 * - **webOS gen ≤3** (Chromium 38, 2017) → `false`. PISO NÃO PROVADO: fica no
 *   mpegts.js até uma prova no Chr38. Conservador > quebrar o fleet 2017.
 * - **webOS gen ≥6** (Chromium 76+, 2021+) → `false` DE PROPÓSITO: motor moderno
 *   onde o mpegts.js/MSE já é rápido — não vale trocar por um caminho que expõe
 *   a credencial no DOM (ver o ramo native-ts do `WebPlayer`) numa classe que
 *   não precisa. Ligar aqui exige prova própria.
 * - **Tizen / Fire TV / Android / VIDAA / web / mobile** → `false`. Nenhuma
 *   provada; `<video>` nativo de TS não é garantido fora do webOS medido.
 */
export function canPlayNativeTs(p: PlatformTierInput): boolean {
  if (p.os !== 'webos') return false
  const gen = webosGeneration(p.version, p.chromiumVersion)
  return gen === 4 || gen === 5
}

/**
 * PREFETCH do filme na ficha (o `VodPrefetcher` do app de TV) ligado por padrão,
 * POR PLATAFORMA. Hoje DESLIGADO em TODAS, de propósito (AP-142 / AP-161).
 *
 * O prefetch baixa o filme num `<video>` escondido enquanto a ficha está aberta,
 * pra o play começar mais rápido. Até o AP-142 ele não tinha como dar ganho (a URL
 * do prefetch não era a do player), e custa: uma conexão ao painel Xtream durante
 * toda a estadia na ficha, MBs por ficha aberta e, na web, banda pela NOSSA
 * infraestrutura (o proxy responde `no-store`, nada é reaproveitado). O ganho só
 * foi visto no pipeline do Chromium com resposta cacheável; no webOS não foi
 * medido. Fica desligado até o A/B do AP-161 medir ganho de TTFF (~300ms na
 * mediana) sem aumentar recusa por `max_connections`.
 *
 * Ligar uma plataforma = trocar o `false` dela aqui, com a medição no changelog.
 * `Record` sobre a união INTEIRA de `Platform`: uma plataforma nova no schema
 * quebra o typecheck aqui até alguém decidir, por escrito, o valor dela.
 *
 * O A/B NÃO mexe nesta tabela: ele usa o override `iptv:ab:vod-prefetch` da
 * capability `vodPrefetch` (ver `capabilities.ts`).
 */
export const VOD_PREFETCH_DEFAULT_BY_PLATFORM: Readonly<Record<Platform, boolean>> = Object.freeze({
  webos: false,
  tizen: false,
  androidtv: false,
  firetv: false,
  vidaa: false,
  desktop: false,
  web: false,
  'mobile-pwa': false,
})

/** O prefetch de filme é ligado por padrão nesta plataforma? Ver a tabela acima. */
export function canPrefetchVod(p: PlatformTierInput): boolean {
  return VOD_PREFETCH_DEFAULT_BY_PLATFORM[p.os] === true
}

/** Rank de um Tier. Helper porque `noUncheckedIndexedAccess` tipa o acesso
 *  indexado como `number | undefined` mesmo num Record total. */
export function rankOf(tier: Tier): number {
  return TIER_RANK[tier] ?? 0
}

/** Aplica a banda a um rank medido (bench/RAM/modelo): trava entre piso e teto.
 *  Teto ANTES do piso: numa banda travada (piso === teto) o piso é a palavra
 *  final, que é o que faz a TV 2019 escapar de um bench de JS pessimista. */
export function clampRankToBand(measuredRank: number, band: TierBand): number {
  let rank = measuredRank
  if (band.ceiling !== null) rank = Math.min(rank, rankOf(band.ceiling))
  if (band.floor !== null) rank = Math.max(rank, rankOf(band.floor))
  return rank
}

/** Idem, em Tier. */
export function clampTierToBand(measured: Tier, band: TierBand): Tier {
  return RANK_TO_TIER[clampRankToBand(rankOf(measured), band)] ?? measured
}
